/**
 * SectionDrawer — per-section vetting drawer for the report editor.
 *
 * Slides in from the right when the analyst clicks a section in the magazine
 * preview. Two stacked panels:
 *
 *   - Edit content: free-form textarea that overrides the AI section text.
 *     Saved as a `position: 'replace'` ReportCustomContent row keyed by the
 *     section. The renderer applies the override over the AI prose.
 *
 *   - Ask Claude: chat-style Q&A against the same writing agent that
 *     authored the report. Pre-loaded with the section's structured-data
 *     context so questions like "where did the 187 come from?" or
 *     "rewrite this in a more cautious tone" return useful answers.
 *
 * The drawer manages the ask conversation locally — it doesn't persist
 * between drawer opens (intentional; keep it scratchpad-style).
 */

import { useEffect, useRef, useState } from 'react';
import {
  X,
  Send,
  Sparkles,
  Loader2,
  AlertTriangle,
  CornerDownLeft,
} from 'lucide-react';
import { Button } from '../ui';
import { askReportSection } from '../../services/reports';

export type SectionDrawerKey =
  | 'cover'
  | 'spread'
  | 'featureIntro'
  | 'chartCaptions'
  | 'statCards'
  | 'retroCards'
  | 'industryCards'
  | 'colophon'
  | string;

interface SectionDrawerProps {
  reportId: string;
  open: boolean;
  sectionKey: SectionDrawerKey | null;
  sectionLabel: string;
  initialOverride: string;
  readOnly: boolean;
  onClose: () => void;
  /**
   * Save the analyst's text override for this section. Should write to the
   * report's customContent as a `position: 'replace'` row keyed by sectionKey.
   * Implementation lives in ReportEditor so it can flow through the same
   * dirty-tracking + Save-edits PUT path used by the sidebar form.
   */
  onSaveOverride: (sectionKey: string, content: string) => void;
}

interface Turn {
  role: 'analyst' | 'agent';
  text: string;
  ts: number;
}

const SUGGESTED_PROMPTS = [
  'Where did the numbers in this section come from?',
  'Rewrite this in a more cautious tone.',
  'Make this one paragraph shorter.',
  'Is anything in this section likely to confuse a non-technical reader?',
];

export function SectionDrawer({
  reportId,
  open,
  sectionKey,
  sectionLabel,
  initialOverride,
  readOnly,
  onClose,
  onSaveOverride,
}: SectionDrawerProps) {
  const [override, setOverride] = useState(initialOverride);
  const [overrideDirty, setOverrideDirty] = useState(false);
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState<Turn[]>([]);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const conversationEndRef = useRef<HTMLDivElement | null>(null);

  // Reset local state whenever the drawer opens onto a different section.
  useEffect(() => {
    if (!open || !sectionKey) return;
    setOverride(initialOverride);
    setOverrideDirty(false);
    setQuestion('');
    setConversation([]);
    setAskError(null);
  }, [open, sectionKey, initialOverride]);

  // Keep the answer scrolled into view as the conversation grows.
  useEffect(() => {
    if (conversation.length > 0) {
      conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [conversation]);

  if (!open || !sectionKey) return null;

  async function handleAsk(prompt?: string) {
    if (!sectionKey) return;
    const q = (prompt ?? question).trim();
    if (!q || asking) return;
    setAsking(true);
    setAskError(null);
    setConversation((prev) => [...prev, { role: 'analyst', text: q, ts: Date.now() }]);
    setQuestion('');
    try {
      const res = await askReportSection(reportId, sectionKey, q);
      if (res.success) {
        setConversation((prev) => [
          ...prev,
          { role: 'agent', text: res.data.answer, ts: Date.now() },
        ]);
      } else {
        setAskError(res.error ?? 'The writing agent did not respond.');
      }
    } catch (err) {
      setAskError(err instanceof Error ? err.message : String(err));
    } finally {
      setAsking(false);
    }
  }

  function handleSaveOverride() {
    if (!sectionKey) return;
    onSaveOverride(sectionKey, override);
    setOverrideDirty(false);
  }

  return (
    <>
      {/* Backdrop — click to close */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed top-0 right-0 h-full w-[460px] bg-bg-secondary border-l border-border-default z-50 shadow-2xl flex flex-col"
        role="dialog"
        aria-label={`Section editor: ${sectionLabel}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-default">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-text-tertiary font-semibold">
              Section
            </div>
            <div className="text-base font-semibold text-text-primary mt-0.5">
              {sectionLabel}
            </div>
            <div className="text-xs text-text-tertiary font-mono mt-0.5">
              {sectionKey}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-secondary"
            aria-label="Close section editor"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Edit override */}
        <div className="px-5 py-4 border-b border-border-default">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-text-primary uppercase tracking-wide">
              Replace this section
            </label>
            {overrideDirty && (
              <span className="text-[11px] text-amber-600">Unsaved</span>
            )}
          </div>
          <textarea
            value={override}
            onChange={(e) => {
              setOverride(e.target.value);
              setOverrideDirty(true);
            }}
            disabled={readOnly}
            placeholder="Leave blank to keep the AI-authored version. Type here to override what the customer will see for this section."
            className="w-full h-28 px-3 py-2 text-sm rounded-lg bg-bg-primary border border-border-default text-text-primary resize-y disabled:opacity-50"
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSaveOverride}
              disabled={readOnly || !overrideDirty}
            >
              Save override
            </Button>
          </div>
        </div>

        {/* Ask Claude */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-5 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-brand-blue" />
              <span className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                Ask the writing agent
              </span>
            </div>
            <p className="text-[11px] text-text-tertiary leading-snug">
              Same model that authored the report. Knows the structured data
              behind every number — ask provenance, request a rewrite, sanity-check tone.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-2">
            {conversation.length === 0 && (
              <div className="space-y-1.5 mt-1">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleAsk(p)}
                    disabled={asking}
                    className="w-full text-left text-xs text-text-secondary border border-border-default hover:border-brand-blue hover:bg-bg-primary rounded-md px-2.5 py-2 transition-colors disabled:opacity-50"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {conversation.map((turn, i) => (
              <div
                key={i}
                className={`mb-3 ${turn.role === 'analyst' ? 'pl-4' : 'pr-4'}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary mb-1">
                  {turn.role === 'analyst' ? 'You' : 'Writing agent'}
                </div>
                <div
                  className={`text-sm whitespace-pre-wrap leading-relaxed rounded-lg px-3 py-2 ${
                    turn.role === 'analyst'
                      ? 'bg-bg-primary text-text-primary border border-border-default'
                      : 'bg-brand-blue/5 text-text-primary border border-brand-blue/20'
                  }`}
                >
                  {turn.text}
                </div>
              </div>
            ))}
            {asking && (
              <div className="flex items-center gap-2 text-xs text-text-tertiary py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Thinking…
              </div>
            )}
            {askError && (
              <div className="flex items-start gap-2 text-xs text-red-600 py-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {askError}
              </div>
            )}
            <div ref={conversationEndRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-border-default px-5 py-3">
            <div className="relative">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleAsk();
                  }
                }}
                placeholder="Ask anything about this section…"
                disabled={asking}
                className="w-full h-16 pr-10 pl-3 py-2 text-sm rounded-lg bg-bg-primary border border-border-default text-text-primary resize-none disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => handleAsk()}
                disabled={!question.trim() || asking}
                className="absolute bottom-2 right-2 p-1.5 rounded-md bg-brand-blue text-white disabled:opacity-30 hover:bg-brand-blue-600"
                aria-label="Send question"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[10px] text-text-tertiary mt-1.5 flex items-center gap-1">
              <CornerDownLeft className="w-3 h-3" />
              <kbd className="font-mono">⌘ + Enter</kbd> to send
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
